/**
 * Thin wrapper around fetch for the Traccar REST API.
 * The Vite proxy handles forwarding /api/* to the Traccar server.
 */

import { translateApiError } from './trackizy';
import { queryString } from './queryString';

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.status = status;
    this.data = data;
    // Mirror @trackizy/api-client's ApiError: a stable machine code the
    // instance-role translator (and any call site) can branch on.
    this.code = data?.code ?? data?.error ?? null;
  }
}

/** Query string for /api/reports/*: arrays go out comma-joined (`deviceId=1,2`), see queryString. */
export const reportParams = (params = {}) => queryString(params);

async function request(url, options = {}) {
  const headers = { ...options.headers };
  // Only set Content-Type for requests with a body (and no explicit Content-Type)
  if (options.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  // Attach JWT Bearer token for Trackizy Server auth
  try {
    const { getAuthToken } = await import('./trackizy.js');
    const token = getAuthToken();
    if (token && !headers['Authorization']) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch { /* trackizy module not available */ }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let data;
    try {
      const text = await response.text();
      try { data = JSON.parse(text); } catch { data = { message: text }; }
    } catch { data = null; }
    // Route every failure through the same instance-role translator the
    // unified client uses, so a support-capability refusal raised on this
    // legacy path (e.g. the geofence delete) shows the translated, actionable
    // toast rather than the raw English server message.
    throw translateApiError(new ApiError(
      data?.message || `Request failed: ${response.status}`,
      response.status,
      data,
    ));
  }

  if (response.status === 204) return null;
  return response.json();
}

const api = {
  // ── Session ──
  getSession: () => request('/api/session'),
  login: async (email, password) => {
    try {
      return await request('/api/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email, password }),
      });
    } catch (err) {
      if (err.status === 401) throw new ApiError('Invalid email or password', 401);
      throw err;
    }
  },
  loginWithToken: (token) => request(`/api/session?token=${encodeURIComponent(token)}`),
  logout: () => request('/api/session', { method: 'DELETE' }),

  // ── Devices ──
  getDevices: () => request('/api/devices'),
  getDevice: (id) => request(`/api/devices/${id}`),
  createDevice: (data) => request('/api/devices', { method: 'POST', body: JSON.stringify(data) }),
  updateDevice: (id, data) => request(`/api/devices/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteDevice: (id) => request(`/api/devices/${id}`, { method: 'DELETE' }),
  updateDeviceAccumulators: (id, data) => request(`/api/devices/${id}/accumulators`, { method: 'PUT', body: JSON.stringify(data) }),

  // ── Positions ──
  // The server exposes two shapes:
  //   GET /api/positions                → latest position per device (org scope)
  //   GET /api/positions/:deviceId?from=…&to=…  → history for one device
  // When callers pass { deviceId, from, to }, we need the second one — the
  // original query-string version silently fell through to the "latest" list
  // on the server and returned useless data for reports (Fuel, Driver Behavior).
  getPositions: (params = {}) => {
    const { deviceId, ...rest } = params;
    if (deviceId && (rest.from || rest.to)) {
      const qs = new URLSearchParams(rest).toString();
      return request(`/api/positions/${deviceId}${qs ? `?${qs}` : ''}`);
    }
    const qs = new URLSearchParams(params).toString();
    return request(`/api/positions${qs ? `?${qs}` : ''}`);
  },

  // ── Users ──
  getUsers: () => request('/api/users'),
  getUser: (id) => request(`/api/users/${id}`),
  createUser: (data) => request('/api/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/api/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteUser: (id) => request(`/api/users/${id}`, { method: 'DELETE' }),

  // ── Groups ──
  getGroups: () => request('/api/groups'),
  createGroup: (data) => request('/api/groups', { method: 'POST', body: JSON.stringify(data) }),
  updateGroup: (id, data) => request(`/api/groups/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteGroup: (id) => request(`/api/groups/${id}`, { method: 'DELETE' }),

  // ── Geofences ──
  getGeofences: () => request('/api/geofences'),
  createGeofence: (data) => request('/api/geofences', { method: 'POST', body: JSON.stringify(data) }),
  updateGeofence: (id, data) => request(`/api/geofences/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteGeofence: (id) => request(`/api/geofences/${id}`, { method: 'DELETE' }),

  // ── Drivers ──
  getDrivers: () => request('/api/drivers'),
  createDriver: (data) => request('/api/drivers', { method: 'POST', body: JSON.stringify(data) }),
  updateDriver: (id, data) => request(`/api/drivers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteDriver: (id) => request(`/api/drivers/${id}`, { method: 'DELETE' }),

  // ── Reports ── (Accept header required or Traccar returns Excel/ZIP)
  // reportParams: `deviceId: [1, 2]` goes out as `deviceId=1,2` (server parseDeviceIds splits commas).
  getTrips: (params) => request(`/api/reports/trips?${reportParams(params)}`, { headers: { Accept: 'application/json' } }),
  getStops: (params) => request(`/api/reports/stops?${reportParams(params)}`, { headers: { Accept: 'application/json' } }),
  getIdling: (params) => request(`/api/reports/idling?${reportParams(params)}`, { headers: { Accept: 'application/json' } }),
  getMaintenanceDue: (params) => request(`/api/reports/maintenance-due?${reportParams(params)}`, { headers: { Accept: 'application/json' } }),
  getSummary: (params) => request(`/api/reports/summary?${reportParams(params)}`, { headers: { Accept: 'application/json' } }),
  getEvents: (params) => request(`/api/reports/events?${reportParams(params)}`, { headers: { Accept: 'application/json' } }),
  getAlerts: (params) => request(`/api/reports/alerts?${reportParams(params)}`, { headers: { Accept: 'application/json' } }),
  // Visits: `deviceId` and `placeId` arrays go out comma-joined; placeType is geofence | poi.
  getVisits: (params) => request(`/api/reports/visits?${reportParams(params)}`, { headers: { Accept: 'application/json' } }),
  // Fuel events (Custom Report Builder's device-capped derived source — see
  // server/src/reports/custom/fuel-events.service.ts). The builder calls
  // this once per device, mirroring getPositions above.
  getFuelEvents: (params) => request(`/api/reports/fuel-events?${new URLSearchParams(params)}`, { headers: { Accept: 'application/json' } }),
  // POI visits (Custom Report Builder's device-capped derived source — see
  // server/src/reports/custom/derive-device-visits.ts). The builder calls
  // this once per device, mirroring getFuelEvents/getPositions above.
  getPoiVisits: (params) => request(`/api/reports/poi-visits?${new URLSearchParams(params)}`, { headers: { Accept: 'application/json' } }),
  // Route replay: the Trackizy Server does NOT expose /api/reports/route
  // (that was Traccar-native). Use /api/positions/:deviceId?from=&to= which
  // returns the same per-fix rows (fixTime, latitude, longitude, speed,
  // course, attributes) that useReplayEngine + replayUtils already consume.
  getRoute: ({ deviceId, from, to }) =>
    request(
      `/api/positions/${encodeURIComponent(deviceId)}?${new URLSearchParams({ from, to })}`,
      { headers: { Accept: 'application/json' } },
    ),

  // ── Commands ──
  getCommands: (deviceId) => request(`/api/commands?deviceId=${deviceId}`),
  getAllCommands: () => request('/api/commands'),
  createCommand: (data) => request('/api/commands', { method: 'POST', body: JSON.stringify(data) }),
  updateCommand: (id, data) => request(`/api/commands/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCommand: (id) => request(`/api/commands/${id}`, { method: 'DELETE' }),
  sendCommand: (data) => request('/api/commands/send', { method: 'POST', body: JSON.stringify(data) }),
  getCommandTypes: (deviceId) => request('/api/commands/types' + (deviceId ? '?deviceId=' + deviceId : '')),

  // ── Maintenance ──
  getMaintenance: () => request('/api/maintenance'),
  createMaintenance: (data) => request('/api/maintenance', { method: 'POST', body: JSON.stringify(data) }),
  updateMaintenance: (id, data) => request(`/api/maintenance/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteMaintenance: (id) => request(`/api/maintenance/${id}`, { method: 'DELETE' }),

  // ── Calendars ──
  getCalendars: () => request('/api/calendars'),
  createCalendar: (data) => request('/api/calendars', { method: 'POST', body: JSON.stringify(data) }),
  updateCalendar: (id, data) => request(`/api/calendars/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCalendar: (id) => request(`/api/calendars/${id}`, { method: 'DELETE' }),

  // ── Sharing ──
  shareDevice: async (deviceId, expiration) => {
    const body = new URLSearchParams({ deviceId, expiration });
    const response = await fetch('/api/devices/share', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
    if (!response.ok) throw new ApiError('Share failed', response.status);
    return response.text(); // Traccar returns a plain text token
  },
  getDeviceShares: (deviceId) => request(`/api/users?deviceId=${deviceId}`),
  deleteShare: (userId) => request(`/api/users/${userId}`, { method: 'DELETE' }),

  // ── Computed Attributes ──
  getComputedAttributes: () => request('/api/attributes/computed'),
  createComputedAttribute: (data) => request('/api/attributes/computed', { method: 'POST', body: JSON.stringify(data) }),
  updateComputedAttribute: (id, data) => request(`/api/attributes/computed/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteComputedAttribute: (id) => request(`/api/attributes/computed/${id}`, { method: 'DELETE' }),
  testComputedAttribute: (deviceId, data) => request(`/api/attributes/computed/test?deviceId=${deviceId}`, { method: 'POST', body: JSON.stringify(data) }),

  // ── Filtered queries for permission linking ──
  getGeofencesByDevice: (deviceId) => request(`/api/geofences?deviceId=${deviceId}`),
  getGeofencesByGroup: (groupId) => request(`/api/geofences?groupId=${groupId}`),
  getGeofencesByUser: (userId) => request(`/api/geofences?userId=${userId}`),
  getDriversByDevice: (deviceId) => request(`/api/drivers?deviceId=${deviceId}`),
  getDriversByGroup: (groupId) => request(`/api/drivers?groupId=${groupId}`),
  getCommandsByDevice: (deviceId) => request(`/api/commands?deviceId=${deviceId}`),
  getDevicesByUser: (userId) => request(`/api/devices?userId=${userId}`),
  getGroupsByUser: (userId) => request(`/api/groups?userId=${userId}`),
  // ── Permissions ──
  linkPermission: (data) => request('/api/permissions', { method: 'POST', body: JSON.stringify(data) }),
  unlinkPermission: (data) => request('/api/permissions', { method: 'DELETE', body: JSON.stringify(data) }),

  // ── Server ──
  getServer: () => request('/api/server'),
  updateServer: (data) => request('/api/server', { method: 'PUT', body: JSON.stringify(data) }),

  // ── Token ── (uses Basic Auth, not session cookies)
  generateToken: async (email, password, expiration) => {
    const response = await fetch('/api/session/token', {
      method: 'POST',
      headers: {
        'Authorization': 'Basic ' + btoa(email + ':' + password),
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ expiration }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new ApiError(text || 'Failed to generate token', response.status);
    }
    return response.text();
  },
};

export { ApiError };
export default api;
